import type { MoneyPageContent } from "./types";
import { FileSearch, AlertTriangle, Layers, Target, TrendingDown, Network } from "lucide-react";

export const seoAuditPage: MoneyPageContent = {
  id: "seo-audit",
  icon: FileSearch,
  serviceType: "SEO Audit",
  esPath: "/servicios/seo-diagnostico",
  enPath: "/en/services/seo-audit",
  es: {
    metaTitle: "Auditoría SEO profesional | Luis ROC",
    metaDescription: "Auditoría SEO que identifica qué limita tu crecimiento orgánico, por qué ocurre y qué priorizar, con recomendaciones documentadas.",
    keywords: ["auditoría seo", "auditoría seo profesional", "análisis seo sitio web", "diagnóstico seo técnico"],
    h1: "Auditoría SEO para descubrir qué está limitando tu crecimiento orgánico",
    breadcrumbLabel: "Auditoría SEO",
    heroSubtitle: "Diagnóstico a fondo, no una lista genérica de errores",
    heroDescription: "Una auditoría SEO profesional responde qué está pasando en tu sitio, por qué está pasando, qué impacto puede tener y qué priorizar primero. No es una revisión superficial: es un análisis documentado que sustenta decisiones.",
    ctaPrimary: { label: "Solicitar información sobre una auditoría SEO", to: "/contacto" },
    ctaSecondary: { label: "Ver casos documentados", to: "/casos-de-exito" },
    summary: [
      { label: "Qué es", text: "Análisis técnico, de contenido y competitivo documentado" },
      { label: "Qué entrega", text: "Hallazgos priorizados y recomendaciones accionables" },
      { label: "Diferencia clave", text: "Va más allá de una conversación inicial: es un análisis en profundidad" },
      { label: "Siguiente paso", text: "Sesión de revisión de resultados incluida" },
    ],
    explanation: {
      title: "Qué responde una auditoría SEO",
      definition: "Una auditoría SEO profesional no es una lista de verificaciones genéricas. Es un análisis que responde cinco preguntas concretas: qué está pasando en el sitio, por qué está pasando, qué impacto tiene o puede tener en el negocio, qué se debe priorizar y cómo medir si las correcciones funcionaron.",
      whyImportant: { title: "Por qué una conversación inicial no basta", text: "Una revisión estratégica inicial sirve para entender el negocio y detectar oportunidades a alto nivel, pero no reemplaza un análisis técnico y de contenido en profundidad. La auditoría profesional entra en el detalle: rastreo, indexación, arquitectura, intención de búsqueda, canibalización y competencia directa." },
      consequences: { title: "Qué pasa si estos problemas no se detectan", text: "Problemas de indexación, contenido duplicado o arquitectura confusa pueden estar frenando el crecimiento durante meses sin que sea evidente desde fuera. Sin un diagnóstico documentado, los equipos terminan invirtiendo en tácticas que no atacan la causa real." },
    },
    problems: {
      title: "Qué se examina en la auditoría",
      subtitle: "Un análisis estructurado en cuatro frentes",
      items: [
        { icon: Layers, title: "Rastreo, indexación y arquitectura", description: "Estado de robots, sitemap, códigos de estado, redirecciones, canonicals y cómo Google interpreta el sitio, incluyendo consideraciones de renderizado con JavaScript.", impact: "Contenido que existe pero no se rastrea ni se indexa correctamente." },
        { icon: AlertTriangle, title: "Contenido e intención de búsqueda", description: "Canibalización entre páginas, duplicación, brechas de contenido frente a lo que la audiencia busca, relevancia temática y enlazado interno.", impact: "Páginas compitiendo entre sí o contenido que no responde a lo que el usuario necesita." },
        { icon: Target, title: "Panorama competitivo", description: "Qué páginas están ganando la búsqueda en tu sector, dónde hay brechas de mercado y oportunidades de autoridad no explotadas.", impact: "Espacios de búsqueda donde la competencia domina sin oposición real." },
        { icon: TrendingDown, title: "Conversión", description: "El camino que recorre un visitante desde que llega por búsqueda hasta que realiza una acción relevante para el negocio.", impact: "Tráfico que llega pero se pierde antes de generar valor." },
      ],
    },
    methodology: {
      title: "Cómo se estructura el trabajo",
      subtitle: "De la revisión estratégica a la auditoría profesional",
      steps: [
        { number: "01", title: "Revisión estratégica inicial", description: "Conversación para entender el negocio, el contexto competitivo y la oportunidad general (no es la auditoría en sí)." },
        { number: "02", title: "Análisis técnico y de contenido", description: "Revisión en profundidad de rastreo, indexación, arquitectura, intención de búsqueda y competencia." },
        { number: "03", title: "Hallazgos documentados", description: "Registro claro de qué se encontró, por qué importa y qué impacto puede tener." },
        { number: "04", title: "Hoja de ruta priorizada", description: "Recomendaciones ordenadas por impacto potencial y esfuerzo de implementación." },
        { number: "05", title: "Sesión de revisión", description: "Repaso de los hallazgos y la hoja de ruta junto a tu equipo, con espacio para preguntas." },
      ],
      ctaText: "Solicitar información sobre una auditoría SEO",
      ctaLink: "/contacto",
    },
    scope: {
      includes: {
        title: "Qué incluye la auditoría",
        items: [
          "Análisis de rastreo, indexación y arquitectura del sitio",
          "Revisión de canonicals, redirecciones, códigos de estado y robots/sitemap",
          "Consideraciones de renderizado JavaScript y, cuando aplica, Core Web Vitals",
          "Análisis de intención de búsqueda, canibalización y duplicación de contenido",
          "Evaluación del panorama competitivo y brechas de mercado",
          "Revisión del camino de conversión desde la búsqueda",
          "Hallazgos documentados y hoja de ruta priorizada",
          "Sesión de revisión de resultados",
        ],
      },
      notIncludes: {
        title: "Qué no incluye",
        items: [
          "No incluye la implementación de las correcciones",
          "No es la reunión inicial gratuita inicial (ese es un paso previo distinto)",
          "No es un reporte automatizado genérico sin análisis humano",
          "No garantiza posiciones ni plazos de resultados",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Para quién tiene sentido",
        items: [
          "Sitios con tráfico estancado o en caída sin causa clara",
          "Empresas antes de una migración o rediseño",
          "Negocios que sospechan problemas técnicos pero no saben cuáles",
          "Equipos que necesitan un documento sólido para justificar inversión en SEO",
        ],
      },
      notFor: {
        title: "Para quién no es el mejor ajuste",
        items: [
          "Quien busca una revisión rápida sin intención de actuar sobre los hallazgos",
          "Sitios nuevos sin historial ni contenido publicado todavía",
          "Quien espera un listado de errores sin contexto de negocio",
        ],
      },
    },
    faq: {
      title: "Preguntas frecuentes sobre la auditoría SEO",
      items: [
        { question: "¿En qué se diferencia de la reunión inicial gratuita?", answer: "La revisión estratégica es una conversación inicial para entender tu negocio y detectar oportunidades a alto nivel. La auditoría profesional es un análisis en profundidad con hallazgos documentados, priorización y recomendaciones concretas." },
        { question: "¿Cuánto cuesta una auditoría SEO?", answer: "Depende del tamaño del sitio, la complejidad técnica y el alcance del análisis. Se cotiza tras conocer el proyecto." },
        { question: "¿Implementan ustedes las recomendaciones?", answer: "La auditoría entrega el diagnóstico y la hoja de ruta. La implementación puede hacerla tu equipo interno, o se puede coordinar por separado según el caso." },
        { question: "¿Cuánto tiempo toma la auditoría?", answer: "Varía según el tamaño y la complejidad del sitio. No hay un plazo fijo universal; se estima al definir el alcance." },
        { question: "¿La auditoría incluye una sesión para revisar los resultados?", answer: "Sí, se incluye una sesión para repasar los hallazgos y la hoja de ruta priorizada junto a tu equipo." },
      ],
    },
    related: {
      title: "Servicios relacionados",
      subtitle: "Otras formas en que puedo ayudar según tu situación",
      links: [
        { label: "Consultoría SEO estratégica", description: "Dirección continua sobre qué priorizar y cómo medirlo.", to: "/consultor-seo" },
        { label: "SEO técnico", description: "Implementación de correcciones sobre rastreo, indexación y arquitectura.", to: "/servicios/seo-tecnico" },
        { label: "Posicionamiento en AI Search", description: "Cómo aparece tu marca en respuestas generadas por IA.", to: "/servicios/posicionamiento-llm" },
        { label: "SEO internacional", description: "Expansión de visibilidad a nuevos países o idiomas.", to: "/servicios/seo-internacional" },
        { label: "SEO local", description: "Estrategia de visibilidad para negocios con presencia física o regional.", to: "/seo-local" },
      ],
    },
    llmSummary: "Auditoría SEO profesional que documenta qué está pasando en el sitio, por qué, qué impacto tiene y qué priorizar, cubriendo aspectos técnicos, de contenido, competitivos y de conversión, distinta de una revisión estratégica inicial gratuita.",
    keyPoints: [
      "Análisis en profundidad, no una lista genérica de errores",
      "Cubre técnico, contenido, competencia y conversión",
      "Entrega hallazgos documentados, hoja de ruta priorizada y sesión de revisión",
      "Distinta de la reunión inicial gratuita inicial",
    ],
  },
  en: {
    metaTitle: "Professional SEO Audit | Luis ROC",
    metaDescription: "An SEO audit that identifies what's limiting your organic growth, why it's happening, and what to prioritize, backed by documented findings.",
    keywords: ["seo audit", "professional seo audit", "website seo analysis", "technical seo audit"],
    h1: "SEO Audit to Uncover What's Limiting Your Organic Growth",
    breadcrumbLabel: "SEO Audit",
    heroSubtitle: "In-depth diagnosis, not a generic checklist",
    heroDescription: "A professional SEO audit answers what's happening on your site, why it's happening, what impact it could have, and what to prioritize first. It's not a surface-level scan — it's a documented analysis that backs real decisions.",
    ctaPrimary: { label: "Ask about a professional SEO audit", to: "/en/contact" },
    ctaSecondary: { label: "See documented work", to: "/en/case-studies" },
    summary: [
      { label: "What it is", text: "A documented technical, content and competitive analysis" },
      { label: "What you get", text: "Prioritized findings and actionable recommendations" },
      { label: "Key distinction", text: "Goes beyond an initial conversation — it's an in-depth analysis" },
      { label: "Next step", text: "A walkthrough session to review the findings is included" },
    ],
    explanation: {
      title: "What a professional SEO audit answers",
      definition: "A professional SEO audit isn't a generic checklist. It's an analysis that answers five specific questions: what's happening on the site, why it's happening, what impact it has or could have on the business, what to prioritize, and how to measure whether fixes worked.",
      whyImportant: { title: "Why an initial conversation isn't enough", text: "A Strategic SEO Review helps understand the business and spot high-level opportunities, but it doesn't replace an in-depth technical and content analysis. A professional audit goes into the detail: crawling, indexing, architecture, search intent, cannibalization, and direct competition." },
      consequences: { title: "What happens when these issues go undetected", text: "Indexing problems, duplicate content, or confusing architecture can be holding back growth for months without it being obvious from the outside. Without a documented diagnosis, teams end up investing in tactics that never address the real cause." },
    },
    problems: {
      title: "What the audit examines",
      subtitle: "A structured analysis across four areas",
      items: [
        { icon: Layers, title: "Crawling, indexing and architecture", description: "The state of robots.txt, sitemap, status codes, redirects, canonicals, and how Google interprets the site, including JavaScript rendering considerations.", impact: "Content that exists but isn't being crawled or indexed properly." },
        { icon: AlertTriangle, title: "Content and search intent", description: "Cannibalization between pages, duplication, content gaps against what the audience is searching for, topical relevance, and internal linking.", impact: "Pages competing with each other, or content that misses what users actually need." },
        { icon: Target, title: "Competitive landscape", description: "Which pages are winning search in your space, where the market gaps are, and untapped authority opportunities.", impact: "Search real estate where competitors dominate unopposed." },
        { icon: TrendingDown, title: "Conversion", description: "The path a visitor takes from arriving via search to taking a meaningful action for the business.", impact: "Traffic that arrives but is lost before generating value." },
      ],
    },
    methodology: {
      title: "How the work is structured",
      subtitle: "From strategic review to professional audit",
      steps: [
        { number: "01", title: "Initial strategic review", description: "A conversation to understand the business, the competitive context and the general opportunity (this is not the audit itself)." },
        { number: "02", title: "Technical and content analysis", description: "In-depth review of crawling, indexing, architecture, search intent and competition." },
        { number: "03", title: "Documented findings", description: "A clear record of what was found, why it matters, and its potential impact." },
        { number: "04", title: "Prioritized roadmap", description: "Recommendations ranked by potential impact and effort to implement." },
        { number: "05", title: "Walkthrough session", description: "A session to walk through the findings and roadmap with your team, with room for questions." },
      ],
      ctaText: "Ask about a professional SEO audit",
      ctaLink: "/en/contact",
    },
    scope: {
      includes: {
        title: "What the audit includes",
        items: [
          "Crawling, indexing and site architecture analysis",
          "Review of canonicals, redirects, status codes, robots.txt and sitemap",
          "JavaScript rendering considerations and, when relevant, Core Web Vitals",
          "Search intent analysis, cannibalization and content duplication review",
          "Competitive landscape assessment and market gap identification",
          "Review of the conversion path from search",
          "Documented findings and a prioritized roadmap",
          "A walkthrough session to review results",
        ],
      },
      notIncludes: {
        title: "What it doesn't include",
        items: [
          "It doesn't include implementing the fixes",
          "It isn't the free 30-minute initial consultation (that's a separate, earlier step)",
          "It isn't an automated generic report without human analysis",
          "It doesn't guarantee rankings or a fixed timeline for results",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Who this is for",
        items: [
          "Sites with stalled or declining traffic and no clear cause",
          "Companies preparing for a migration or redesign",
          "Businesses that suspect technical issues but can't pinpoint them",
          "Teams that need a solid document to justify SEO investment",
        ],
      },
      notFor: {
        title: "Who this isn't the best fit for",
        items: [
          "Anyone looking for a quick scan with no intent to act on the findings",
          "New sites with no history or published content yet",
          "Anyone expecting a list of errors with no business context",
        ],
      },
    },
    faq: {
      title: "Frequently asked questions about the SEO audit",
      items: [
        { question: "How is this different from the free 30-minute initial consultation?", answer: "The Strategic Review is an initial conversation to understand your business and spot high-level opportunities. The professional audit is an in-depth analysis with documented findings, prioritization and concrete recommendations." },
        { question: "How much does a professional SEO audit cost?", answer: "It depends on the site's size, technical complexity, and the scope of the analysis. Pricing is provided after learning about the project." },
        { question: "Do you implement the recommendations?", answer: "The audit delivers the diagnosis and the roadmap. Implementation can be handled by your internal team, or coordinated separately depending on the case." },
        { question: "How long does the audit take?", answer: "It varies based on the site's size and complexity. There's no universal fixed timeline — it's estimated once the scope is defined." },
        { question: "Does the audit include a session to review the results?", answer: "Yes, a walkthrough session is included to go over the findings and the prioritized roadmap with your team." },
      ],
    },
    related: {
      title: "Related services",
      subtitle: "Other ways I can help depending on your situation",
      links: [
        { label: "Strategic SEO consulting", description: "Ongoing direction on what to prioritize and how to measure it.", to: "/en/seo-consultant" },
        { label: "Technical SEO", description: "Implementing fixes for crawling, indexing and architecture.", to: "/en/services/technical-seo" },
        { label: "AI Search positioning", description: "How your brand shows up in AI-generated answers.", to: "/en/services/llm-positioning" },
        { label: "International SEO", description: "Expanding visibility into new countries or languages.", to: "/en/services/international-seo" },
        { label: "Local SEO", description: "Visibility strategy for businesses with physical or regional presence.", to: "/en/local-seo" },
      ],
    },
    llmSummary: "A professional SEO audit documenting what's happening on the site, why, what impact it has, and what to prioritize, covering technical, content, competitive and conversion aspects, distinct from a free initial strategic review.",
    keyPoints: [
      "In-depth analysis, not a generic error checklist",
      "Covers technical, content, competitive and conversion factors",
      "Delivers documented findings, a prioritized roadmap and a walkthrough session",
      "Distinct from the free 30-minute initial consultation",
    ],
  },
};
